// User role management system
const ROLES_STORAGE_KEY = 'chess_user_roles';
const USER_SESSIONS_KEY = 'chess_user_sessions';

// Define role hierarchy and permissions
export const ROLES = {
  SUPER_ADMIN: {
    id: 'super_admin',
    name: 'Super Administrator',
    level: 100,
    permissions: ['*'], // All permissions
    description: 'Full system access with all privileges'
  },
  ADMIN: {
    id: 'admin',
    name: 'Administrator',
    level: 80,
    permissions: [
      'user_management',
      'leaderboard_management',
      'system_settings',
      'view_analytics',
      'export_data',
      'manage_tournaments',
      'moderate_content'
    ],
    description: 'Administrative access with user and system management'
  },
  MODERATOR: {
    id: 'moderator',
    name: 'Moderator',
    level: 60,
    permissions: [
      'view_analytics',
      'moderate_content',
      'manage_user_stats',
      'view_reports'
    ],
    description: 'Content moderation and basic user management'
  },
  PREMIUM_USER: {
    id: 'premium_user',
    name: 'Premium User',
    level: 40,
    permissions: [
      'advanced_stats',
      'export_personal_data',
      'priority_support',
      'custom_themes',
      'extended_history'
    ],
    description: 'Premium features and enhanced experience'
  },
  USER: {
    id: 'user',
    name: 'Regular User',
    level: 20,
    permissions: [
      'play_games',
      'view_leaderboard',
      'view_personal_stats',
      'basic_features'
    ],
    description: 'Standard user with basic features'
  },
  GUEST: {
    id: 'guest',
    name: 'Guest',
    level: 10,
    permissions: [
      'play_games',
      'view_leaderboard'
    ],
    description: 'Limited access for non-registered users'
  }
};

// Permission definitions
export const PERMISSIONS = {
  USER_MANAGEMENT: 'user_management',
  LEADERBOARD_MANAGEMENT: 'leaderboard_management',
  SYSTEM_SETTINGS: 'system_settings',
  VIEW_ANALYTICS: 'view_analytics',
  EXPORT_DATA: 'export_data',
  MANAGE_TOURNAMENTS: 'manage_tournaments',
  MODERATE_CONTENT: 'moderate_content',
  ADVANCED_STATS: 'advanced_stats',
  EXPORT_PERSONAL_DATA: 'export_personal_data',
  PRIORITY_SUPPORT: 'priority_support',
  CUSTOM_THEMES: 'custom_themes',
  EXTENDED_HISTORY: 'extended_history',
  PLAY_GAMES: 'play_games',
  VIEW_LEADERBOARD: 'view_leaderboard',
  VIEW_PERSONAL_STATS: 'view_personal_stats',
  BASIC_FEATURES: 'basic_features',
  MANAGE_USER_STATS: 'manage_user_stats',
  VIEW_REPORTS: 'view_reports'
};

// Get user roles data
export const getUserRolesData = () => {
  try {
    const data = localStorage.getItem(ROLES_STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error loading user roles:', error);
    return {};
  }
};

// Save user roles data
export const saveUserRolesData = (data) => {
  try {
    localStorage.setItem(ROLES_STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving user roles:', error);
    return false;
  }
};

// Get user role
export const getUserRole = (username) => {
  const rolesData = getUserRolesData();
  return rolesData[username] || ROLES.USER.id;
};

// Set user role
export const setUserRole = (username, roleId) => {
  const roleExists = Object.values(ROLES).some(role => role.id === roleId);
  if (!roleExists) {
    console.error('Invalid role:', roleId);
    return false;
  }

  const rolesData = getUserRolesData();
  rolesData[username] = roleId;
  const success = saveUserRolesData(rolesData);

  if (success) {
    console.log(`Set role for ${username} to ${roleId}`);
  }

  return success;
};

// Check if user has permission
export const hasPermission = (username, permission) => {
  const userRoleId = getUserRole(username);
  const userRole = Object.values(ROLES).find(role => role.id === userRoleId);

  if (!userRole) {
    console.log(`No role found for user: ${username}`);
    return false;
  }

  // Super admin has all permissions
  if (userRole.permissions.includes('*')) {
    return true;
  }

  const hasPermissionResult = userRole.permissions.includes(permission);
  console.log(`User ${username} (${userRole.name}) has permission ${permission}:`, hasPermissionResult);
  return hasPermissionResult;
};

// Check if user has role level
export const hasRoleLevel = (username, requiredLevel) => {
  const userRoleId = getUserRole(username);
  const userRole = Object.values(ROLES).find(role => role.id === userRoleId);

  if (!userRole) return false;

  return userRole.level >= requiredLevel;
};

// Get role by ID
export const getRoleById = (roleId) => {
  return Object.values(ROLES).find(role => role.id === roleId);
};

// Get all users with their roles
export const getAllUsersWithRoles = () => {
  const rolesData = getUserRolesData();
  const leaderboardData = JSON.parse(localStorage.getItem('chess_leaderboard_data') || '{}');

  const users = Object.keys(leaderboardData).map(username => {
    const roleId = rolesData[username] || ROLES.USER.id;
    const role = getRoleById(roleId);
    return {
      username,
      role: role || ROLES.USER,
      stats: leaderboardData[username],
      lastActive: leaderboardData[username]?.lastPlayed || null
    };
  });

  return users.sort((a, b) => b.role.level - a.role.level);
};

// Promote user to higher role
export const promoteUser = (username, newRoleId) => {
  const currentRoleId = getUserRole(username);
  const currentRole = getRoleById(currentRoleId);
  const newRole = getRoleById(newRoleId);

  if (!currentRole || !newRole) return false;

  // Check if it's actually a promotion
  if (newRole.level <= currentRole.level) {
    console.error('Cannot promote to same or lower role level');
    return false;
  }

  return setUserRole(username, newRoleId);
};

// Demote user to lower role
export const demoteUser = (username, newRoleId) => {
  const currentRoleId = getUserRole(username);
  const currentRole = getRoleById(currentRoleId);
  const newRole = getRoleById(newRoleId);

  if (!currentRole || !newRole) return false;

  // Check if it's actually a demotion
  if (newRole.level >= currentRole.level) {
    console.error('Cannot demote to same or higher role level');
    return false;
  }

  return setUserRole(username, newRoleId);
};

// Get users by role
export const getUsersByRole = (roleId) => {
  const rolesData = getUserRolesData();
  return Object.entries(rolesData)
    .filter(([username, userRoleId]) => userRoleId === roleId)
    .map(([username]) => username);
};

// Initialize role system with default admin
export const initializeRoleSystem = () => {
  try {
    const rolesData = getUserRolesData();

    // Set default super admin role for 'admin' user if not exists
    if (!rolesData.admin) {
      rolesData.admin = ROLES.SUPER_ADMIN.id;
      console.log('Setting admin user as super_admin');
    }

    // Set default admin role for 'moderator' user if not exists
    if (!rolesData.moderator) {
      rolesData.moderator = ROLES.ADMIN.id;
      console.log('Setting moderator user as admin');
    }

    const success = saveUserRolesData(rolesData);
    console.log('Role system initialized:', success);
    return success;
  } catch (error) {
    console.error('Error initializing role system:', error);
    return false;
  }
};

// Session management
export const createUserSession = (username, roleId) => {
  try {
    const sessions = JSON.parse(localStorage.getItem(USER_SESSIONS_KEY) || '{}');
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    sessions[sessionId] = {
      username,
      roleId,
      createdAt: Date.now(),
      lastActive: Date.now(),
      permissions: getRoleById(roleId)?.permissions || []
    };

    localStorage.setItem(USER_SESSIONS_KEY, JSON.stringify(sessions));
    sessionStorage.setItem('current_session_id', sessionId);

    return sessionId;
  } catch (error) {
    console.error('Error creating user session:', error);
    return null;
  }
};

// Get current session
export const getCurrentSession = () => {
  try {
    const sessionId = sessionStorage.getItem('current_session_id');
    if (!sessionId) return null;

    const sessions = JSON.parse(localStorage.getItem(USER_SESSIONS_KEY) || '{}');
    return sessions[sessionId] || null;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
};

// Update session activity
export const updateSessionActivity = () => {
  try {
    const sessionId = sessionStorage.getItem('current_session_id');
    if (!sessionId) return false;

    const sessions = JSON.parse(localStorage.getItem(USER_SESSIONS_KEY) || '{}');
    if (sessions[sessionId]) {
      sessions[sessionId].lastActive = Date.now();
      localStorage.setItem(USER_SESSIONS_KEY, JSON.stringify(sessions));
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error updating session activity:', error);
    return false;
  }
};

// Clear user session
export const clearUserSession = () => {
  try {
    const sessionId = sessionStorage.getItem('current_session_id');
    if (sessionId) {
      const sessions = JSON.parse(localStorage.getItem(USER_SESSIONS_KEY) || '{}');
      delete sessions[sessionId];
      localStorage.setItem(USER_SESSIONS_KEY, JSON.stringify(sessions));
    }

    sessionStorage.removeItem('current_session_id');
    return true;
  } catch (error) {
    console.error('Error clearing user session:', error);
    return false;
  }
};

// Clean expired sessions
export const cleanExpiredSessions = () => {
  try {
    const sessions = JSON.parse(localStorage.getItem(USER_SESSIONS_KEY) || '{}');
    const now = Date.now();
    const sessionTimeout = 24 * 60 * 60 * 1000; // 24 hours
    const activeSessions = {};

    Object.entries(sessions).forEach(([sessionId, session]) => {
      if (now - session.lastActive < sessionTimeout) {
        activeSessions[sessionId] = session;
      }
    });

    localStorage.setItem(USER_SESSIONS_KEY, JSON.stringify(activeSessions));
    return true;
  } catch (error) {
    console.error('Error cleaning expired sessions:', error);
    return false;
  }
};